import {
  ConsultCreate,
  EpisodeCreateRequest,
  IntakeObservationRequest,
  IntakeQuestionnaireInner,
  IntakeRequest,
  PatientRequest,
  Intake,
  ConsultStatusChangeNewStatusEnum,
  Consult,
  PatientUI,
  PatientUIRequest,
  Patient,
} from "@futurhealth/steadymd-api-client";
import axios from "axios";
import { SMDClinician, SMDEpisode } from "../typings";
import { User } from "firebase/auth";

export const STEADY_MD_PROGRAM_GUID = import.meta.env
  .VITE_STEADY_MD_PROGRAM_GUID;
export const STEADY_MD_ASYNC_PROGRAM_NAME = import.meta.env
  .VITE_STEADY_MD_ASYNC_PROGRAM_NAME;

const steadymdCloudFunctionsBaseURL = `${import.meta.env.VITE_API_GATEWAY_URL}/steadymd`;

export const preferredMedicationNameQuestionData = {
  category: "",
  description: "",
  index: -1,
  question: "Offered Bundle",
  question_type: "",
  slug: "preferred_medication_name",
};

export const priorAuthData = {
  category: "",
  description: "",
  index: -1,
  question: "Prior auth",
  question_type: "",
  slug: "prior_auth",
};

export const preferredPartnershipPharmacy = {
  category: "",
  description: "",
  index: 0,
  question: "Preferred partnership pharmacy",
  question_type: "",
  slug: "preferred_partnership_pharmacy",
};

export const getSteadymdPatient = async (
  patientId: string | undefined,
  user: User | null
) => {
  if (!patientId) throw new Error("User is not authenticated");
  if (!user) throw new Error("User is not authenticated");
  const token = await user.getIdToken();
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { patientId },
  };

  const response = await axios.get<Patient>(
    `${steadymdCloudFunctionsBaseURL}/patient/retrieve`,
    config
  );
  return response.data;
};

export const createSteadymdPatient = async (
  patient: PatientRequest,
  token: string
) => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const payload = { ...patient, address2: patient.address2 || "" };

  const response = await axios.post(
    `${steadymdCloudFunctionsBaseURL}/patient/create`,
    payload,
    config
  );
  return response.data;
};

export const createSteadymdEpisode = async (
  episode: EpisodeCreateRequest,
  token: string
) => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const response = await axios.post(
    `${steadymdCloudFunctionsBaseURL}/episode/create`,
    episode,
    config
  );
  return response.data;
};

export const updateSteadymdEpisode = async (
  episodeId: string,
  updatedData: Partial<SMDEpisode>,
  token: string
) => {
  if (!episodeId) {
    throw new Error("episodeId is required");
  }
  if (!updatedData) {
    throw new Error("updatedData is required");
  }
  const url = `${steadymdCloudFunctionsBaseURL}/episode/update?episodeId=${episodeId}`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  try {
    const response = await axios.patch<{ guid: string }>(
      url,
      updatedData,
      config
    );
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to update SteadyMD episode: ${(error as Error).message}`
    );
  }
};

export const createSteadymdConsult = async (
  consult: ConsultCreate,
  token?: string,
  isAsync?: boolean
): Promise<Consult> => {
  if (!token) throw new Error("User is not authenticated");
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const payload = {
    ...consult,
    ...(isAsync !== undefined && { isAsync }),
  };

  const response = await axios.post(
    `${steadymdCloudFunctionsBaseURL}/consult/create`,
    payload,
    config
  );
  return response.data;
};

export const scheduleSteadymdConsult = async (
  consultId: string,
  startsAt: string,
  timezone: string,
  token: string
): Promise<Consult> => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const response = await axios.post(
    `${steadymdCloudFunctionsBaseURL}/consult/schedule`,
    { consultId, startsAt, timezone },
    config
  );
  return response.data;
};

export const updateSteadymdConsultStatus = async (
  consultId: string,
  status: ConsultStatusChangeNewStatusEnum,
  token: string
): Promise<Consult> => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { consultId },
  };

  const response = await axios.post(
    `${steadymdCloudFunctionsBaseURL}/consult/update-status`,
    { status },
    config
  );
  return response.data;
};

/**
 * Creates a SteadyMD intake with retry logic for transient failures.
 *
 * Idempotency: Application-level protection via atomicSetIntakeGuid transaction
 * ensures only one intake per asyncSession. Retries only 5xx server errors;
 * 4xx client errors (including duplicates) are not retried.
 *
 * @param episodeGuid - SteadyMD episode GUID
 * @param observations - Medical observations array
 * @param questionnaire - Intake questionnaire responses
 * @param labRequisitionGuid - Optional lab requisition GUID
 * @param isDraft - Whether this is a draft intake
 * @param token - Firebase auth token
 * @returns Created intake data with guid
 */
export const createSteadymdIntake = async (
  episodeGuid: string,
  observations: Array<IntakeObservationRequest>,
  questionnaire: Array<IntakeQuestionnaireInner>,
  labRequisitionGuid: string | null,
  isDraft: boolean,
  token: string
) => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000, // 30 second timeout
  };

  // Retry logic with linear backoff for intake creation (1s, 2s delays)
  const maxRetries = 2;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.post(
        `${steadymdCloudFunctionsBaseURL}/intake/create`,
        {
          episodeGuid,
          observations,
          questionnaire,
          labRequisitionGuid,
          isDraft,
        },
        config
      );
      return response.data;
    } catch (error) {
      lastError = error as Error;

      // Don't retry on client errors (4xx) - only retry on network/server errors
      if (
        axios.isAxiosError(error) &&
        error.response?.status &&
        error.response.status < 500
      ) {
        console.error(
          "Client error creating intake, not retrying:",
          error.response?.status,
          error.response?.data?.error || error.response?.data?.message
        );
        throw error;
      }

      if (attempt < maxRetries) {
        const delayMs = 1000 * (attempt + 1); // Linear backoff: 1s, 2s
        console.warn(
          `Intake API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delayMs}ms...`,
          {
            status: axios.isAxiosError(error)
              ? error.response?.status
              : undefined,
            code: axios.isAxiosError(error) ? error.code : undefined,
            message: (error as Error).message,
          }
        );
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      } else {
        // On final retry, log error metadata only (no PII/PHI)
        console.error("All intake creation retries exhausted:", {
          attempts: maxRetries + 1,
          responseStatus: axios.isAxiosError(lastError)
            ? lastError.response?.status
            : undefined,
          errorCode: axios.isAxiosError(lastError)
            ? lastError.response?.data?.error || lastError.response?.data?.code
            : undefined,
          errorMessage: lastError?.message,
        });
      }
    }
  }

  // Preserve the actual error details for debugging
  if (axios.isAxiosError(lastError) && lastError.response?.data) {
    const apiError = lastError.response.data;
    const errorMessage =
      apiError.error || apiError.message || "Unknown API error";
    throw new Error(`Intake creation failed: ${errorMessage}`);
  }

  throw lastError || new Error("Failed to create intake after retries");
};

/**
 * Data structure for intake file uploads to SteadyMD
 */
export interface IntakeFileData {
  /** The unique identifier for the intake this file belongs to */
  intakeGuid: string;
  /** The file to be uploaded (image or PDF) */
  file: File;
  /**
   * The type/purpose of the file being uploaded:
   * - identification: Government ID or insurance card
   * - selfie: Patient photo for identity verification
   * - diagnostic: Body photos for clinical assessment
   * - lab_result: Laboratory test results or reports
   * - other: Miscellaneous medical documents
   */
  kind: "identification" | "selfie" | "diagnostic" | "lab_result" | "other";
}

export const createSteadyMdIntakeFile = async (
  intakeFiles: IntakeFileData[],
  token: string
): Promise<unknown> => {
  const url = `${steadymdCloudFunctionsBaseURL}/intakefile/create`;
  const formData = new FormData();

  if (!token) {
    throw new Error("User is not authenticated");
  }

  if (!intakeFiles?.length) {
    throw new Error("No intake files provided");
  }

  console.log("createSteadyMdIntakeFile called with:", {
    fileCount: intakeFiles.length,
    files: intakeFiles.map(({ file, kind, intakeGuid }) => ({
      intakeGuid,
      kind,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    })),
  });

  // Add all files to the FormData (use 'file' as field name for each)
  intakeFiles.forEach(({ file }) => {
    formData.append("file", file, file.name);
  });

  // Create metadata array for all files
  const fileMetadata = intakeFiles.map((fileData) => ({
    intakeGuid: fileData.intakeGuid,
    kind: fileData.kind,
  }));

  // Add metadata as JSON string
  formData.append("fileMetadata", JSON.stringify(fileMetadata));

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    console.log("Sending FormData to:", url);
    const response = await axios.post(url, formData, config);
    console.log("Upload response:", {
      status: response.status,
      data: response.data,
    });

    // Check if the backend response indicates failure
    if (!response.data?.success) {
      throw new Error(
        response.data?.message ||
          "Photo upload failed - no success status returned"
      );
    }

    // Check for partial failures
    if (response.data?.data?.results) {
      const failedUploads = response.data.data.results.filter(
        (result: { status: string }) => result.status === "failed"
      );
      if (failedUploads.length > 0) {
        const errorMessages = failedUploads
          .map((failed: { error: string }) => failed.error)
          .join(", ");
        throw new Error(`Some photo uploads failed: ${errorMessages}`);
      }
    }

    return response.data;
  } catch (err) {
    console.error("Upload error:", {
      message: (err as Error).message,
      response: (err as { response?: { data?: unknown } })?.response?.data,
      status: (err as { response?: { status?: number } })?.response?.status,
    });
    throw new Error(`Failed to upload intake files: ${(err as Error).message}`);
  }
};

export const getConsultAvailabilities = async (
  consultId: string,
  token: string
) => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { consultId },
  };

  const response = await axios.get(
    `${steadymdCloudFunctionsBaseURL}/consult-availabilities/retrieve`,
    config
  );

  return response.data;
};

export const getAvailabilitiesForProgram = async (
  usState: string,
  consultType: string,
  isAsync: boolean,
  token: string
) => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { usState, consultType, isAsync },
  };

  const response = await axios.get(
    `${steadymdCloudFunctionsBaseURL}/program-consult-availabilities/retrieve`,
    config
  );

  return response.data;
};

/**
 * Updates an existing SteadyMD intake with retry logic for transient failures.
 *
 * Idempotency: Update operations are naturally idempotent - repeated updates
 * with same data produce same result. Retries only 5xx server errors; 4xx
 * client errors are not retried.
 *
 * @param intakeId - Existing intake GUID to update
 * @param updatedIntake - Updated intake data
 * @param token - Firebase auth token
 * @returns Updated intake data
 */
export const updateIntake = async (
  intakeId: string,
  updatedIntake: IntakeRequest,
  token: string
) => {
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { intakeId },
    timeout: 30000,
  };

  // Retry logic with linear backoff for intake updates (1s, 2s delays)
  const maxRetries = 2;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.post<Intake>(
        `${steadymdCloudFunctionsBaseURL}/intake/update`,
        updatedIntake,
        config
      );
      return response.data;
    } catch (error) {
      lastError = error as Error;

      if (
        axios.isAxiosError(error) &&
        error.response?.status &&
        error.response.status < 500
      ) {
        console.error(
          "Client error updating intake, not retrying:",
          error.response?.status,
          error.response?.data?.error || error.response?.data?.message
        );
        throw error;
      }

      if (attempt < maxRetries) {
        const delayMs = 1000 * (attempt + 1);
        console.warn(
          `Intake update failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delayMs}ms...`,
          {
            status: axios.isAxiosError(error)
              ? error.response?.status
              : undefined,
            code: axios.isAxiosError(error) ? error.code : undefined,
            message: (error as Error).message,
          }
        );
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      } else {
        console.error("All intake update retries exhausted:", {
          attempts: maxRetries + 1,
          responseStatus: axios.isAxiosError(lastError)
            ? lastError.response?.status
            : undefined,
          errorCode: axios.isAxiosError(lastError)
            ? lastError.response?.data?.error || lastError.response?.data?.code
            : undefined,
          errorMessage: lastError?.message,
        });
      }
    }
  }

  if (axios.isAxiosError(lastError) && lastError.response?.data) {
    const apiError = lastError.response.data;
    const errorMessage =
      apiError.error || apiError.message || "Unknown API error";
    throw new Error(`Intake update failed: ${errorMessage}`);
  }

  throw lastError || new Error("Failed to update intake after retries");
};

export const updateConsultStatus = async (
  token: string,
  consultId: string,
  postBody: { status: string | null }
) => {
  const url = `${steadymdCloudFunctionsBaseURL}/consult/update-status?consultId=${consultId}`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };
  const response = await axios.post<{
    guid: string;
  }>(url, postBody, config);
  return response.data;
};

export const generatePatientUIURLForVisit = async (
  token: string,
  consultId: string,
  postBody: PatientUIRequest
) => {
  const url = `${steadymdCloudFunctionsBaseURL}/patient-ui-url/create`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { consultId },
  };
  const response = await axios.post<PatientUI>(url, postBody, config);
  return response.data;
};

export const getLabOrderPDF = async (labOrderId: string, token: string) => {
  const url = `${steadymdCloudFunctionsBaseURL}/lab-order-pdf/retrieve`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { labOrderId },
  };

  const response = await axios.get(url, config);
  return response.data;
};

export const getLabOrderResultPDF = async (
  labOrderId: string,
  labOrderResultId: string,
  token: string
) => {
  const url = `${steadymdCloudFunctionsBaseURL}/lab-order-result-pdf/retrieve`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    params: { labOrderId, labOrderResultId },
  };

  const response = await axios.get(url, config);
  return response.data;
};

export const retrieveClinician = async (token: string, consultId: string) => {
  const url = `${steadymdCloudFunctionsBaseURL}/clinician/retrieve?clinicianId=${consultId}`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };
  const response = await axios.get<SMDClinician>(url, config);
  return response.data;
};

export const createMessage = async (
  token: string,
  userId: string,
  episodeGuid: string,
  messageText: string,
  isAsync: boolean,
  signal: AbortSignal
) => {
  const url = `${steadymdCloudFunctionsBaseURL}/message/create`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const response = await axios.post(
    url,
    { userId, episodeGuid, messageText, isAsync },
    { ...config, signal }
  );
  return response.data;
};

export const getPreferredMedicationNameAnswer = (medicationName: string) => {
  return {
    type: "text",
    answerType: "valueString",
    question: "Offered Bundle",
    answer: [medicationName],
  } as IntakeQuestionnaireInner;
};
